import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  ArrowLeft, 
  MapPin, 
  Clock, 
  Check, 
  Star,
  CalendarIcon,
  Users,
  Phone,
  Mail,
  CheckCircle2,
  Tag
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TripDetail() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState({
    footer_phone: '91 125 63 52 / 678 744 482',
    footer_email: 'reservas@viajesensueno.com'
  });
  const [date, setDate] = useState();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    num_travelers: 2,
    special_requests: ''
  });

  useEffect(() => {
    const fetchTrip = async () => {
      try {
        const res = await axios.get(`${API}/trips/${tripId}`);
        setTrip(res.data);
        // SEO
        if (res.data) {
          document.title = `${res.data.title} | Viajes de Ensueño`;
          const description = res.data.description?.substring(0, 160) || `Descubre nuestro viaje a ${res.data.destination}. Experiencias únicas diseñadas a medida.`;
          document.querySelector('meta[name="description"]')?.setAttribute("content", description);
        }
      } catch (err) {
        console.error('Error fetching trip:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTrip();

    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        if (res.data) {
          setSettings(prev => ({
            ...prev,
            footer_phone: res.data.footer_phone || prev.footer_phone,
            footer_email: res.data.footer_email || prev.footer_email
          }));
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };
    fetchSettings();
  }, [tripId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error('Por favor selecciona una fecha de viaje');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...formData,
        trip_id: trip.id,
        trip_title: trip.title,
        travel_date: format(date, 'yyyy-MM-dd')
      });
      toast.success('¡Solicitud enviada! Te contactaremos pronto.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        num_travelers: 2,
        special_requests: ''
      });
      setDate(undefined);
    } catch (err) {
      toast.error('Error al enviar la solicitud. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="pt-32 pb-20">
          <div className="container-custom">
            <div className="skeleton h-96 w-full rounded-2xl mb-8" />
            <div className="skeleton h-8 w-1/2 mb-4" />
            <div className="skeleton h-4 w-3/4" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!trip) {
    return (
      <Layout>
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-2xl font-serif text-primary">Viaje no encontrado</h1>
          <Link to="/" className="btn-primary mt-6 inline-block">
            Volver al inicio
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Image */}
      <div className="relative h-[60vh] min-h-[400px]">
        <img
          src={trip.image_url}
          alt={trip.title}
          className="w-full h-full object-cover"
        />
        <div className="hero-gradient absolute inset-0" />
        
        <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-16">
          <div className="container-custom">
            <button
              onClick={() => navigate(-1)}
              data-testid="back-link"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver
            </button>
            
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <span className="px-4 py-1 bg-secondary/20 text-secondary rounded-full text-sm font-medium">
                {trip.category}
              </span>
              <span className="flex items-center gap-2 text-white/80">
                <MapPin className="w-4 h-4" />
                {trip.destination}
              </span>
              <span className="flex items-center gap-2 text-white/80">
                <Clock className="w-4 h-4" />
                {trip.duration}
              </span>
            </div>

            <h1 
              className="text-4xl lg:text-6xl font-serif text-white mb-4"
              data-testid="trip-title"
            >
              {trip.title}
            </h1>

            <div className="flex items-center gap-4">
              <div className="text-3xl font-semibold text-accent">
                Desde {trip.price.toLocaleString('es-ES')}€
              </div>
              {trip.discount && (
                <div className="bg-accent text-white px-4 py-1.5 rounded-full font-bold text-sm shadow-lg flex items-center gap-2 animate-pulse">
                  <Tag className="w-4 h-4" />
                  OFERTA: {trip.discount}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Description */}
              <div>
                <h2 className="text-2xl font-serif font-semibold text-primary mb-4">
                  Sobre este viaje
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed whitespace-pre-line">
                  {trip.description}
                </p>
              </div>

              {/* Highlights */}
              {trip.highlights && trip.highlights.length > 0 && (
                <div>
                  <h2 className="text-2xl font-serif font-semibold text-primary mb-6">
                    Destacados
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {trip.highlights.map((highlight, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 p-4 bg-secondary/10 rounded-xl"
                      >
                        <Star className="w-5 h-5 text-accent flex-shrink-0" />
                        <span className="text-primary">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Includes */}
              {trip.includes && trip.includes.length > 0 && (
                <div>
                  <h2 className="text-2xl font-serif font-semibold text-primary mb-6">
                    El viaje incluye
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {trip.includes.map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                          <Check className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="text-muted-foreground">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Seccion Conoce a tu Asesor (Condicional desde Backend) */}
              {trip.show_advisor && (
                <div className="bg-primary text-white p-8 md:p-12 rounded-[3.5rem] mt-12 relative overflow-hidden shadow-2xl">
                  <div className="absolute top-0 right-0 p-12 opacity-10">
                    <Users className="w-48 h-48" />
                  </div>
                  <div className="flex flex-col md:flex-row items-center gap-8 relative z-10">
                    <div className="w-40 h-40 rounded-full border-4 border-accent/30 overflow-hidden shrink-0 shadow-2xl">
                      <img 
                        src={trip.advisor_image || "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80&w=400"} 
                        alt={trip.advisor_name || "Asesor Experto"}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="text-center md:text-left space-y-3">
                      <div className="inline-block bg-accent text-primary px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest shadow-lg">
                        Tu Experta en {trip.destination}
                      </div>
                      <h3 className="text-3xl font-serif">"Hola, soy {trip.advisor_name || 'Elena'}"</h3>
                      <p className="text-white/70 max-w-xl italic font-serif leading-relaxed">
                        {trip.advisor_bio || `Este itinerario no es el resultado de un algoritmo, sino de mis propios viajes a ${trip.destination}. He seleccionado personalmente cada hotel y cada experiencia para que vivas algo auténtico.`}
                      </p>
                      <div className="pt-4 flex flex-wrap justify-center md:justify-start gap-4">
                        <div className="flex items-center gap-2 text-xs bg-white/5 border border-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                          <CheckCircle2 className="w-4 h-4 text-accent" />
                          Trato Directo
                        </div>
                        <div className="flex items-center gap-2 text-xs bg-white/5 border border-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                          <CheckCircle2 className="w-4 h-4 text-accent" />
                          Atención 24/7
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Booking Form */}
            <div className="lg:col-span-1">
              <div 
                className="bg-white rounded-2xl shadow-xl p-8 sticky top-28"
                data-testid="booking-form"
              >
                <h3 className="text-xl font-serif font-semibold text-primary mb-6">
                  Reserva tu viaje
                </h3>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="form-label">Nombre completo</label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Tu nombre"
                      data-testid="booking-name"
                      className="form-input"
                    />
                  </div>

                  <div>
                    <label className="form-label">Email</label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="tu@email.com"
                      data-testid="booking-email"
                      className="form-input"
                    />
                  </div>

                  <div>
                    <label className="form-label">Teléfono</label>
                    <Input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+34 612 345 678"
                      data-testid="booking-phone"
                      className="form-input"
                    />
                  </div>

                  <div>
                    <label className="form-label">Número de viajeros</label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      required
                      value={formData.num_travelers}
                      onChange={(e) => setFormData({ ...formData, num_travelers: parseInt(e.target.value) })}
                      data-testid="booking-travelers"
                      className="form-input"
                    />
                  </div>

                  <div>
                    <label className="form-label">Fecha deseada</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          data-testid="booking-date"
                          className="w-full justify-start text-left font-normal form-input"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, "PPP", { locale: es }) : "Selecciona fecha"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <label className="form-label">Peticiones especiales (opcional)</label>
                    <Textarea
                      value={formData.special_requests}
                      onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                      placeholder="Cuéntanos tus preferencias..."
                      data-testid="booking-requests"
                      className="form-textarea"
                      rows={4}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="booking-submit"
                    className="w-full btn-accent py-6"
                  >
                    {submitting ? 'Enviando...' : 'Solicitar Información'}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t border-slate-100 space-y-3">
                  <a
                    href={`tel:${settings.footer_phone.split('/')[0].trim()}`}
                    className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    {settings.footer_phone}
                  </a>
                  <a
                    href={`mailto:${settings.footer_email}`}
                    className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Mail className="w-5 h-5" />
                    {settings.footer_email}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="h-32 bg-slate-50" />
    </Layout>
  );
}
